import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'
import { Card, CardHead } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import type { BaseCurrencyResponse } from '~/state/base-currency'
import { currencyChoices, useBaseCurrency, useSaveBaseCurrency } from '~/state/base-currency'

const CSS = `
.nw-base-currency p { margin: 0 0 var(--nw-space-3); }
.nw-base-currency .nw-lede { color: var(--nw-body-ink); font-size: var(--nw-text-cell); max-width: 70ch; }
.nw-base-currency .nw-decide { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-3); margin-top: var(--nw-space-4); }
.nw-base-currency label > span { color: var(--nw-muted); display: block; font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-base-currency select { min-width: 260px; }
.nw-base-currency .nw-error { color: var(--nw-critical); font-size: var(--nw-text-cell); margin: 0; }
.nw-base-currency .nw-unsaved { color: var(--nw-body-ink); font-size: var(--nw-text-chip); margin: 0; }
.nw-base-currency .nw-note { color: var(--nw-muted); font-size: var(--nw-text-chip); max-width: 70ch; }
.nw-base-currency .nw-waiting { color: var(--nw-body-ink); font-size: var(--nw-text-cell); max-width: 70ch; }
`

/**
 * What every figure in the product is denominated in.
 *
 * Switching it reprices the past rather than relabelling it: each stored amount
 * carries its own currency and is converted at the rate of its own date. What
 * that needs is rates, and `fx_rate` is keyed by the currency being converted
 * into — so the first switch to a currency starts with a rate for no date at
 * all. The save fetches them, and this card reports what came back, because the
 * alternative reading of the same moment is a total that has quietly shrunk.
 */
export function BaseCurrencyPanel(): ReactElement {
  const current = useBaseCurrency()
  const save = useSaveBaseCurrency()
  const [chosen, setChosen] = useState<string | null>(null)

  const choices = useMemo(currencyChoices, [])
  const stored = current.data?.value
  const shown = chosen ?? stored ?? ''
  const edited = stored !== undefined && shown !== stored
  const busy = current.isPending || save.isPending

  // What the household is waiting for right now: after a save, what that save
  // came back with; before one, what the stored reading already knows is
  // unconvertible — a switch made in an earlier session that never completed.
  const waiting = save.data?.backfill.unresolved ?? current.data?.coverage.unresolved ?? []

  return (
    <Card className="nw-base-currency">
      <style>{CSS}</style>
      <CardHead
        id="nw-base-currency-heading"
        title="The currency everything is counted in"
        lede="Every total, chart and threshold in the product is stated in this. Changing it does not relabel the past: each amount is converted at the rate of its own date, so history is restated in the new currency rather than carried across at today's rate."
      />

      {current.error && (
        <p className="nw-error">Could not read the currency: {current.error.message}</p>
      )}
      {save.error && <p className="nw-error">Could not save: {save.error.message}</p>}

      <div className="nw-decide">
        <label>
          <span>Counted in</span>
          <select
            value={shown}
            disabled={busy}
            aria-label="The currency every figure is counted in"
            onChange={(event) => setChosen(event.target.value)}
          >
            {/* A select whose value matches no option shows the first one
                instead, which would name a currency the household has never
                held while the read is still in flight. */}
            {shown === '' && <option value="">Reading…</option>}
            {choices.map((choice) => (
              <option key={choice.code} value={choice.code}>
                {choice.label}
              </option>
            ))}
          </select>
        </label>
        {/* Reachable only where the row was deleted outright: every household
            is created holding one. Worth saying rather than showing the
            fallback as though somebody had picked it. */}
        {current.data?.stored === false && <StatusPill tone="caution" label="Not chosen yet" />}
        <Button
          kind="primary"
          disabled={busy || !edited}
          onClick={() => save.mutate(shown, { onSuccess: () => setChosen(null) })}
        >
          {save.isPending ? 'Fetching rates…' : 'Save currency'}
        </Button>
        {edited && !save.isPending && (
          <p className="nw-unsaved">Not saved yet — no figure has moved.</p>
        )}
      </div>

      {save.isPending && (
        <p className="nw-waiting">
          Fetching the exchange rates this currency needs, back to the first day anything was held.
          Until they are on record, any figure that has to cross a currency is left out of the
          total rather than guessed at, so the numbers read low while this runs.
        </p>
      )}

      {save.data && !save.isPending && <Outcome result={save.data} />}

      {!save.isPending && save.data === undefined && waiting.length > 0 && (
        <p className="nw-waiting">
          There is still no exchange rate for {waiting.join(', ')}. Anything held in those
          currencies is left out of every total until the rates arrive, which the next nightly run
          will try again for. Health lists them too.
        </p>
      )}

      {/*
        The one figure on screen that does not follow this switch, said here
        because it is correct and would otherwise read as the switch having
        half worked.
      */}
      <p className="nw-note">
        Year-end pins keep the currency they were read in. Each one records the figures a tax
        return was filed on, so it goes on saying what it said rather than being restated here.
      </p>
    </Card>
  )
}

function Outcome({ result }: { result: BaseCurrencyResponse }): ReactElement {
  const { backfill } = result

  if (backfill.error !== undefined) {
    return (
      <p className="nw-waiting">
        Saved: everything is now counted in {result.value}. The exchange rates it needs could not
        be fetched — {backfill.error} — so history that has to cross a currency is left out of
        every total for now, and reads low. The next nightly run tries again.
      </p>
    )
  }

  if (backfill.unresolved.length > 0) {
    return (
      <p className="nw-waiting">
        Saved: everything is now counted in {result.value}. There is still no rate for{' '}
        {backfill.unresolved.join(', ')}, so anything held in those currencies is left out of
        every total rather than guessed at. The next nightly run tries again.
      </p>
    )
  }

  if (backfill.written === 0) {
    return (
      <p className="nw-waiting">
        Saved: everything is now counted in {result.value}. Every rate it needs was already on
        record, so the figures are right now.
      </p>
    )
  }

  return (
    <p className="nw-waiting">
      Saved: everything is now counted in {result.value}. {backfill.written} exchange rates were
      fetched, back to {backfill.from}, so history is restated in {result.value} now rather than
      after tonight's run.
    </p>
  )
}
